"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircleQuestionMark } from "lucide-react";
const faqs = [{
  question: "What technology is required for virtual sessions?",
  answer: "We utilize Zoom for high-definition video conferencing and an interactive digital whiteboard. Students only need a computer with a stable internet connection and a webcam. A tablet with a stylus is recommended for the best collaborative experience but not required."
}, {
  question: "How do I schedule a tutoring session?",
  answer: "Scheduling is handled through our integrated portal. You can book recurring weekly slots or individual sessions based on availability. Once a session is confirmed, you will receive an automated calendar invite with your unique classroom link."
}, {
  question: "What is the difference between an MA and NBCT?",
  answer: "An MA in Teaching Mathematics represents advanced academic mastery of pedagogy. National Board Certification (NBCT) is the highest professional distinction in K-12 education, requiring a rigorous, multi-year peer-review process that proves a teacher's ability to improve student learning."
}, {
  question: "Do you offer small group sessions or only 1-on-1?",
  answer: "I offer both. One-on-one sessions provide maximum personalization, while small groups (2-4 students) are excellent for peers in the same course who benefit from collaborative problem-solving and a more social learning environment."
}, {
  question: "How do you track student progress?",
  answer: "We use a combination of formative assessments during sessions and periodic progress reports. Parents receive monthly updates detailing concepts mastered, areas for continued focus, and growth in mathematical confidence."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221646" id="common-questions" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-4 flex items-center justify-center gap-2 text-primary">
            <MessageCircleQuestionMark className="size-6" />
            <span className="font-default text-sm font-semibold uppercase tracking-wider">
              Academic Support
            </span>
          </div>
          <h2 className="font-serif text-3xl font-bold tracking-tight text-primary sm:text-4xl lg:text-5xl">
            Common Questions
          </h2>
          <p className="mt-4 font-default text-lg text-muted-foreground">Everything you need to know about our virtual math tutoring process and the <br />Berkeley standard of excellence.</p>
        </div>

        <div ref={listRef} className={`transition-all duration-700 delay-200 ease-out ${isListVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Accordion type="single" collapsible className="w-full rounded-lg border border-border bg-card shadow-lg">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border px-6 transition-colors data-[state=open]:bg-muted/50">
                <AccordionTrigger className="font-default text-base font-semibold text-primary hover:no-underline hover:text-primary/80 data-[state=open]:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-default text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className={`mt-12 flex flex-col items-center justify-center gap-4 text-center transition-all duration-700 delay-400 ease-out sm:flex-row ${isListVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <p className="font-default text-muted-foreground">
            Have a specific question about your curriculum?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-md">
            <Link to="#lets-build-confidence">Contact Mr. Cottone</Link>
          </Button>
        </div>
      </div>
    </section>;
}